import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { fetchBlogBySlug, formatBlogDate, estimateReadingTime } from '../services/blogService';
import { ArrowLeftIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { renderCtaButton } from '../utils/ctaHelper';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { cta, domainConfig } = useTheme();
  const { variant, styles } = useVariant();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadBlog = async () => {
      try {
        setLoading(true);
        const blogData = await fetchBlogBySlug(slug);
        if (blogData) {
          setBlog(blogData);
        } else {
          setError('Blog not found');
        }
      } catch (err) {
        setError('Failed to load blog');
        console.error('Error loading blog:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadBlog();
    }
  }, [slug]);

  // Use centralized variant styles
  const variantClasses = {
    container: styles.container,
    hero: styles.heroBg,
    card: styles.card,
    button: styles.button,
    buttonSecondary: styles.buttonSecondary,
    text: styles.text,
    textSecondary: styles.textSecondary,
    accent: styles.accent,
    section: styles.section
  };

  if (loading) {
    return (
      <div className={`pt-16 ${variantClasses.container}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className={`text-lg ${variantClasses.textSecondary}`}>Loading blog...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className={`pt-16 ${variantClasses.container}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className={`text-3xl font-bold mb-4 ${variantClasses.text}`}>
              {error || 'Blog Not Found'}
            </h1>
            <p className={`text-lg mb-8 ${variantClasses.textSecondary}`}>
              The blog you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate('/blog')}
              className={`px-6 py-3 font-semibold rounded-lg transition-all duration-300 ${variantClasses.button}`}
            >
              Back to Blog
            </button>
          </div>
        </div>
      </div>
    );
  }

  const readingTime = estimateReadingTime(blog.sections, blog.conclusion);

  return (
    <div className={`pt-16 ${variantClasses.container}`}>
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/blog')}
          className={`flex items-center text-sm font-medium transition-colors duration-300 ${
            variant === 'modern' ? 'text-purple-400 hover:text-purple-300' :
            variant === 'creative' ? 'text-orange-500 hover:text-orange-400' :
            variant === 'elegant' ? 'text-blue-600 hover:text-blue-500' :
            variant === 'dark' ? 'text-blue-400 hover:text-blue-300' :
            'text-blue-600 hover:text-blue-500'
          }`}
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Blog
        </button>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <article className={`rounded-2xl shadow-lg overflow-hidden ${variantClasses.card}`}>
          {/* Featured Image */}
          <div className="relative h-64 md:h-96 overflow-hidden">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/assets/default-fallback.svg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          </div>

          {/* Article Header */}
          <div className="p-8 md:p-12">
            <div className="flex items-center mb-6 space-x-4">
              <div className="flex items-center text-sm">
                <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" />
                <span className={variantClasses.textSecondary}>
                  {formatBlogDate(blog.date)}
                </span>
              </div>
              <div className="flex items-center text-sm">
                <ClockIcon className="w-4 h-4 mr-2 text-gray-500" />
                <span className={variantClasses.textSecondary}>
                  {readingTime}
                </span>
              </div>
            </div>

            <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight ${variantClasses.text}`}>
              {blog.title}
            </h1>

            <div className={`text-xl leading-relaxed mb-8 ${variantClasses.textSecondary}`}>
              {blog.excerpt}
            </div>
          </div>
        </article>
      </div>

      {/* Article Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className={`prose prose-lg max-w-none ${variantClasses.text}`}>
          {blog.sections && blog.sections.map((section, index) => (
            <div key={index} className="mb-8">
              <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${variantClasses.accent}`}>
                {section.heading}
              </h2>
              <div className={`text-lg leading-relaxed whitespace-pre-line ${variantClasses.textSecondary}`}>
                {section.content}
              </div>
            </div>
          ))}

          {blog.conclusion && (
            <div className={`mt-12 p-8 rounded-2xl ${
              variant === 'modern' ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200/50 dark:border-purple-700/50' :
              variant === 'creative' ? 'bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30 border border-orange-200/50 dark:border-orange-700/50' :
              variant === 'elegant' ? 'bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/50 border border-slate-200/50 dark:border-slate-600/50' :
              variant === 'dark' ? 'bg-gradient-to-r from-gray-800/50 to-slate-800/50 border border-gray-700/50' :
              variant === 'corporate' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200/50 dark:border-blue-700/50' :
              variant === 'landing' ? 'bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/30' :
              'bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800/50 dark:to-blue-900/30 border border-gray-200/50 dark:border-gray-600/50'
            }`}>
              <div className="flex items-center mb-6">
                <div className={`w-2 h-8 rounded-full mr-4 ${
                  variant === 'modern' ? 'bg-gradient-to-b from-purple-500 to-indigo-500' :
                  variant === 'creative' ? 'bg-gradient-to-b from-orange-500 to-red-500' :
                  variant === 'elegant' ? 'bg-gradient-to-b from-slate-600 to-gray-600' :
                  variant === 'dark' ? 'bg-gradient-to-b from-blue-400 to-blue-500' :
                  variant === 'corporate' ? 'bg-gradient-to-b from-blue-600 to-indigo-600' :
                  variant === 'landing' ? 'bg-gradient-to-b from-blue-500 to-purple-500' :
                  'bg-gradient-to-b from-blue-500 to-blue-600'
                }`}></div>
                <h2 className={`text-2xl md:text-3xl font-bold ${variantClasses.accent}`}>
                  Conclusion
                </h2>
              </div>
              <div className={`text-lg leading-relaxed ${
                variant === 'modern' ? 'text-slate-700 dark:text-slate-300' :
                variant === 'creative' ? 'text-gray-700 dark:text-gray-300' :
                variant === 'elegant' ? 'text-slate-700 dark:text-slate-300' :
                variant === 'dark' ? 'text-gray-200' :
                variant === 'corporate' ? 'text-slate-700 dark:text-slate-300' :
                variant === 'landing' ? 'text-white/90' :
                'text-gray-700 dark:text-gray-300'
              }`}>
                {blog.conclusion}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Related Articles or CTA */}
      <div className={`relative overflow-hidden ${
        variant === 'minimal' ? 'bg-gray-50' :
        variant === 'modern' ? 'bg-slate-800/50' :
        variant === 'corporate' ? 'bg-gray-50' :
        variant === 'creative' ? 'bg-white/50' :
        variant === 'elegant' ? 'bg-blue-50' :
        variant === 'landing' ? 'bg-gradient-to-r from-blue-800 to-purple-800' :
        variant === 'dark' ? 'bg-gray-800/50' :
        'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${variantClasses.accent}`}>
              Ready to Get Started?
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${variantClasses.textSecondary}`}>
              Discover how our solutions can help transform your fleet management operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {renderCtaButton(cta?.primary, 'Request Demo', `px-8 py-4 font-semibold rounded-lg transition-all duration-300 ${variantClasses.button}`, domainConfig?.shopLink)}
              {renderCtaButton(cta?.secondary, 'Buy Now', `px-8 py-4 font-semibold rounded-lg transition-all duration-300 text-center ${variantClasses.buttonSecondary}`, domainConfig?.shopLink)}
              <button
                onClick={() => navigate('/solutions')}
                className={`px-8 py-4 font-semibold rounded-lg transition-all duration-300 ${variantClasses.buttonSecondary}`}
              >
                View Solutions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
